/**
 * CaseStudyShowcase.jsx
 * ==============================================================================
 *
 * Lädt alle Beta-Tester-Cards vom Public-Endpoint und zeigt sie als
 * responsive Karten-Reihe.
 *
 * Datenfluss:
 *   1. GET /api/admin/case-study/public/list          → Customer-IDs
 *   2. Für jede ID: GET /api/admin/case-study/public/:id/card-data
 *   3. Render <CaseStudyCard /> pro erfolgreich geladenem Datensatz
 *
 * Sichtbar werden nur Customer, die:
 *   - Werbevereinbarung (§ 2) akzeptiert haben UND
 *   - vom Admin im Configurator explizit für die Website freigegeben wurden
 *     (Toggle "Auf smartratings.ai anzeigen").
 *
 * Default ist FALSE — ein Customer erscheint NIE automatisch.
 *
 * Layout-Limits:
 *   - Auf Desktop max. `maxOnDesktop` Cards (default: 3)
 *   - Auf Mobile  max. `maxOnMobile` Cards (default: 2)
 *   - Falls mehr Cards geladen werden: "Mehr Erfolgsgeschichten →"-Link unten
 *     (nur sichtbar wenn `seeMoreLink` gesetzt und tatsächlich mehr Cards da sind).
 *   - Mit `maxOnDesktop={Infinity}` werden alle Cards gezeigt (z.B. auf der
 *     /erfolgsgeschichten-Übersichtsseite).
 *
 * Auto-Fill mit Werbe-Platzhaltern (`placeholderFillTo`):
 *   - Wenn echte Cards < placeholderFillTo → mit CaseStudyPlaceholder auffüllen,
 *     damit das Grid nicht halb leer aussieht.
 *   - Beispiel: placeholderFillTo=2, 1 echte Card → 1 Placeholder angezeigt.
 *   - Default ist 0 (kein Auffüllen) — Placeholders sind nur sinnvoll auf der
 *     Homepage, nicht auf der /erfolgsgeschichten-Seite.
 *
 * Responsives Grid:
 *   - < 800px (Mobile):       1 Spalte
 *   - 800-1199px (Tablet):    2 Spalten
 *   - ≥ 1200px (Desktop):     3 Spalten
 *
 * Die Section wird komplett ausgeblendet, wenn keine Cards verfügbar sind.
 * Ausnahme: wenn `placeholderFillTo > 0` und Cards leer, KEINE Platzhalter zeigen —
 * die Section bleibt versteckt, weil ein reines "Werbe-Karusell" ohne echte Cards
 * irreführend wäre.
 *
 * @author Claude & Christian
 * @date   2026-05-14 (initial)
 * @date   2026-05-14 (Limits + See-More-Link)
 * @date   2026-05-14 (Placeholder Auto-Fill)
 */

import React, { useEffect, useState } from 'react';
import CaseStudyCard from './CaseStudyCard';
import CaseStudyPlaceholder from './CaseStudyPlaceholder';
import './CaseStudyShowcase.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  'https://api.smartratings.ai';

async function fetchCustomerIds(signal) {
  try {
    const res = await fetch(`${API_BASE}/api/admin/case-study/public/list`, { signal });
    if (!res.ok) return [];
    const json = await res.json();
    if (!json.success || !Array.isArray(json.customerIds)) return [];
    return json.customerIds;
  } catch (err) {
    if (err.name !== 'AbortError') {
      console.warn('[CaseStudyShowcase] list fetch failed:', err.message);
    }
    return [];
  }
}

async function fetchCardData(customerId, signal) {
  try {
    const res = await fetch(
      `${API_BASE}/api/admin/case-study/public/${customerId}/card-data`,
      { signal }
    );
    if (!res.ok) return null;
    const json = await res.json();
    if (!json.success) return null;
    return json;
  } catch (err) {
    if (err.name !== 'AbortError') {
      console.warn(`[CaseStudyShowcase] fetch ${customerId} failed:`, err.message);
    }
    return null;
  }
}

/**
 * @param {Object} props
 * @param {string} [props.title]
 * @param {string} [props.subtitle]
 * @param {number} [props.maxOnDesktop=3]      - Max. Cards auf Desktop (≥1200px)
 * @param {number} [props.maxOnMobile=2]       - Max. Cards auf Mobile (<800px)
 * @param {number} [props.placeholderFillTo=0] - Fülle Cards mit Platzhaltern auf
 *                                                diese Anzahl auf, wenn weniger
 *                                                echte Cards verfügbar sind.
 * @param {string} [props.seeMoreLink]         - Wenn gesetzt + es gibt mehr Cards
 *                                                als Desktop-Limit: CTA-Link.
 * @param {string} [props.seeMoreLabel]        - Label für den CTA-Link
 * @param {number} [props.expectedCount=2]     - Anzahl Skeleton-Platzhalter während Loading
 */
export default function CaseStudyShowcase({
  title,
  subtitle,
  maxOnDesktop = 3,
  maxOnMobile = 2,
  placeholderFillTo = 0,
  seeMoreLink,
  seeMoreLabel = 'Mehr Erfolgsgeschichten →',
  expectedCount = 2
}) {
  const [cards, setCards] = useState(null); // null = loading, [] = nichts, [...] = geladen

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const ids = await fetchCustomerIds(controller.signal);
      if (controller.signal.aborted) return;
      if (ids.length === 0) {
        setCards([]);
        return;
      }
      const results = await Promise.all(
        ids.map(id => fetchCardData(id, controller.signal))
      );
      if (controller.signal.aborted) return;
      setCards(results.filter(Boolean));
    })();
    return () => controller.abort();
  }, []);

  // Loading: Skeleton-Platzhalter
  // Wichtig: data-cells + data-max-mobile setzen, damit die Skelette an der
  // gleichen Stelle wie die echten Cards positioniert sind (sonst rutschen sie
  // links bündig). expectedCount sollte zur erwarteten Final-Anzahl passen
  // (echte Cards + Platzhalter), damit kein Layout-Shift beim Laden auftritt.
  // Skelette ab Index >= maxOnMobile bekommen die csc-cell-placeholder-Klasse,
  // damit sie auf Tablet ausgeblendet werden (analog zu echten Platzhaltern
  // später — verhindert, dass eine einzelne Cell alleine in Reihe 2 landet).
  if (cards === null) {
    const skeletonCount = Math.max(1, Math.min(expectedCount, maxOnDesktop));
    const skeletonMobile = Math.min(maxOnMobile, skeletonCount);
    return (
      <section className="csc-showcase">
        <div className="csc-showcase-container">
          {title && <h2 className="csc-showcase-title">{title}</h2>}
          {subtitle && <p className="csc-showcase-subtitle">{subtitle}</p>}
          <div
            className="csc-showcase-grid"
            data-cells={skeletonCount}
            data-max-mobile={skeletonMobile}
          >
            {Array.from({ length: skeletonCount }).map((_, i) => {
              const isTabletExtra = i >= maxOnMobile;
              return (
                <div
                  key={i}
                  className={`csc-showcase-cell${isTabletExtra ? ' csc-cell-placeholder' : ''}`}
                  data-index={i}
                >
                  <div className="csc-skeleton" />
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Nichts da → Section komplett verstecken (auch wenn placeholderFillTo > 0,
  // damit nicht eine reine Werbeplatzhalter-Reihe ohne echten Social Proof erscheint)
  if (cards.length === 0) return null;

  // Slice: auf das größere Limit kürzen (CSS versteckt zusätzliche auf Mobile).
  // Bei maxOnDesktop=Infinity (Übersichtsseite) werden alle Cards gerendert.
  const displayLimit = Math.max(maxOnDesktop, maxOnMobile);
  const displayCards = Number.isFinite(displayLimit)
    ? cards.slice(0, displayLimit)
    : cards;

  // Auto-Fill: Wenn weniger echte Cards als placeholderFillTo, fülle mit Platzhaltern auf
  const placeholderCount = Math.max(0, placeholderFillTo - displayCards.length);

  // "Mehr"-Link nur zeigen wenn (a) Link gesetzt und (b) auf Desktop nicht alle echten Cards sichtbar
  const showMoreLink = Boolean(seeMoreLink) && cards.length > maxOnDesktop;

  // Mobile-Limit: Echte Cards + Platzhalter zusammen — Platzhalter zählen mit,
  // damit auf Mobile nicht aus Versehen alle Platzhalter zusätzlich erscheinen.
  // (Praktisch: bei placeholderFillTo=2, maxOnMobile=2 → 1 Card + 1 Platzhalter ist genau richtig.)
  const totalCells = displayCards.length + placeholderCount;
  const effectiveMaxMobile = Math.min(maxOnMobile, totalCells);

  return (
    <section className="csc-showcase">
      <div className="csc-showcase-container">
        {title && <h2 className="csc-showcase-title">{title}</h2>}
        {subtitle && <p className="csc-showcase-subtitle">{subtitle}</p>}
        <div
          className="csc-showcase-grid"
          data-cells={totalCells}
          data-max-mobile={effectiveMaxMobile}
        >
          {displayCards.map((data, i) => (
            <div
              key={`card-${data.customer?.id || i}`}
              className="csc-showcase-cell"
              data-index={i}
            >
              <CaseStudyCard data={data} />
            </div>
          ))}
          {Array.from({ length: placeholderCount }).map((_, i) => (
            <div
              key={`placeholder-${i}`}
              className="csc-showcase-cell csc-cell-placeholder"
              data-index={displayCards.length + i}
            >
              <CaseStudyPlaceholder />
            </div>
          ))}
        </div>

        {showMoreLink && (
          <div className="csc-showcase-more">
            <a href={seeMoreLink} className="csc-showcase-more-link">
              {seeMoreLabel}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
